import { ChangeEvent, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import CheckboxTree from 'react-checkbox-tree';
import type { Node } from 'react-checkbox-tree';
import { cx } from '@linaria/core';
import { flex } from 'styles/utils/core';
import { Button } from 'components/ui_library/Button';
import { Switch } from 'components/Switch';
import { ScrollWrapper } from 'components/Dialog/ScrollWrapper';
import { useEffectOnce, useToggle } from 'react-use';
import { noop } from 'utils/fp';
import { SCOPE_GROUPS_LIST, BASIC_DESCRIPTIONS } from './scopesGraphUtils';
import { checkboxTreeList, scrollWrapper, scopeModeButton, selectAllElement } from './ScopesList.styles';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { ScopesListStore, createScopesListStore } from './ScopesList.store';
import { ScopesListProps } from './types';
interface ScopesListInternalProps extends ScopesListProps {
  store: ScopesListStore;
}
type TreeNode = {
  icon: boolean;
  label: string;
  value: string;
  name?: string;
  children?: Array<TreeNode>;
};
export const ScopesListInternal: React.FC<ScopesListInternalProps> = observer(({
  store,
  onScopesChanged,
  isDisabled = false,
  isAdvanced = false
}) => {
  const [isAdvancedMode, toggleIsAdvancedMode] = useToggle(isAdvanced);

  // The "expanded scope groups" are simply the scope groups being shown under advanced view.
  // Check out its usage in <CheckboxTree /> below.
  const [expandedScopeGroups, setExpandedScopeGroups] = useState<string[]>(isAdvanced ? SCOPE_GROUPS_LIST : []);

  // "nodes" are used in the nested checkbox list. Check out its usage in <CheckboxTree /> below.
  const nodes: Array<TreeNode> = useMemo(() => {
    return SCOPE_GROUPS_LIST.map(scopeGroup => {
      // Remove the "_" suffix part from scope group's name
      const groupName = scopeGroup.split('_')?.[0];
      return {
        title: 'scope-group',
        name: groupName,
        label: BASIC_DESCRIPTIONS[groupName] || groupName,
        value: scopeGroup,
        icon: false,
        children: store.graph.adjacent(scopeGroup).map(childNode => ({
          title: 'scope-item',
          label: childNode,
          value: childNode,
          icon: false
        }))
      };
    });
  }, [store.graph]);
  const toggleAdvancedMode = useCallback(() => {
    toggleIsAdvancedMode();
    setExpandedScopeGroups(previousExpandedList => !previousExpandedList?.length ? SCOPE_GROUPS_LIST : []);
  }, [toggleIsAdvancedMode, setExpandedScopeGroups]);
  const notifyScopesChanged = useCallback(() => {
    if (store.isAllScopesSelected) {
      onScopesChanged(['All']);
    } else {
      onScopesChanged(store.checkedScopesWithoutScopeGroups);
    }
  }, [onScopesChanged, store]);
  const toggleSelectAllClbk = useCallback(({
    target: {
      checked: selectAllScopes
    }
  }: ChangeEvent<HTMLInputElement>) => {
    store.toggleSelectAll(selectAllScopes);
    notifyScopesChanged();
  }, [store]);
  const toggleSelectAll = isDisabled ? noop : toggleSelectAllClbk;
  const handleOnCheckboxClbk = useCallback((_, {
    value: scopeName
  }: Node) => {
    store.handleCheckboxClick(scopeName);
    notifyScopesChanged();
  }, [store]);
  const handleOnCheckboxClick = isDisabled ? noop : handleOnCheckboxClbk;
  useEffectOnce(() => {
    notifyScopesChanged();
  });
  return <div className={cx(flex.init, flex.direction.column, 'fullWidth')}>
        {!isDisabled && <div className={cx(flex.init, flex.alignItems.center, flex.justifyContent.between)}>
            <div className={selectAllElement}>
              <Switch isActive={store.isAllScopesSelected} label={`${store.isAllScopesSelected ? 'Unselect' : 'Select'} All Scopes`} onChange={toggleSelectAll} />
            </div>
            <Button data-testid="mode-selection-btn" variant="unstyled" onClick={toggleAdvancedMode} className={scopeModeButton}>
              {`${isAdvancedMode ? 'Basic' : 'Advanced'} scopes`}
            </Button>
          </div>}

        <ScrollWrapper className={scrollWrapper}>
          <div className={checkboxTreeList}>
            <CheckboxTree nativeCheckboxes nodes={nodes} checked={store.checkedScopes} expanded={expandedScopeGroups} expandDisabled={!isAdvancedMode} onExpand={setExpandedScopeGroups} onCheck={handleOnCheckboxClick} icons={{
          expandOpen: null,
          expandClose: null,
          leaf: null
        }} />
          </div>
        </ScrollWrapper>
      </div>;
});
export const ScopesList: React.FC<ScopesListProps> = props => {
  const store = useMemo(() => createScopesListStore(props), [props.existingScopes, props.scopeDeps, props.endpointDeps]);
  return <ScopesListInternal store={store} {...props} />;
};